/**
 * CorrespondentAdminTab — the back-office fields that used to sit on Profile:
 * logo URL, email domains, onboarding volume estimates, bank details and the
 * appraisal lender names.
 *
 * Same shell as the Profile tab (`PricingPageLayout` + its "On this page" rail,
 * `ProfileSection` cards, `PricingFieldText` labels) so the two read as one
 * page split in two. Saves through the same `POST /correspondents/{companyId}/update`
 * partial, sending ONLY the keys this tab owns — Profile sends its own set, so
 * the two tabs' saves never overwrite each other.
 */
import { useCallback, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

import AddRounded from "@mui/icons-material/AddRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";

import Flx from "../../../components/layout/Flx";
import { status as statusColors } from "../../../theme/colors";
import {
  PricingPageLayout,
  PricingFieldText as PricingField,
  T as PT,
} from "../../admin/AdminPricingScreen/pricingUi";
import { FIELD_TEXT } from "../../../theme/formStyles";
import PendingChangesBar from "../../../components/common/PendingChangesBar";
import { Cell, FIELD_SX, Grid, ProfileSection } from "./correspondentForm";

// Anchor rail — mirrors the section ids below, same as PROFILE_RAIL. Kept
// alphabetical (A–Z), and the sections render in the same order.
const ADMIN_RAIL = [
  { id: "admin-appraisal", label: "Appraisal names" },
  { id: "admin-bank", label: "Bank details" },
  { id: "admin-branding", label: "Branding" },
  { id: "admin-domains", label: "Email domains" },
  { id: "admin-volumes", label: "Onboarding volumes" },
];

// `BankDetails.bankAccountOwnerType` — verbatim from the model's `options`.
const BANK_OWNER_TYPES = ["Individual", "Multiple Individuals", "Entity"];

// Public providers can't be registered as a corporate domain (the backend
// rejects them at signup); catching it here saves a round trip.
const FREE_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
  "aol.com",
  "icloud.com",
];

const num = (v) => (v === "" || v == null ? null : Number(v));

/** Removable chip for the two string-list editors. */
function Chip({ label, onRemove }) {
  return (
    <Flx
      ac
      g={0.5}
      sx={{
        borderRadius: "999px",
        border: `1px solid ${PT.line}`,
        background: PT.surface2,
        pl: 1.5,
        pr: 0.5,
        py: 0.25,
        fontSize: "12.5px",
        color: PT.ink2,
      }}
    >
      {label}
      <IconButton
        size="small"
        aria-label={`Remove ${label}`}
        onClick={onRemove}
        sx={{ p: 0.25 }}
      >
        <CloseRounded sx={{ fontSize: 14 }} />
      </IconButton>
    </Flx>
  );
}

export default function CorrespondentAdminTab({ detail, patch }) {
  const buildDraft = useCallback(
    () => ({
      companyLogo: detail.companyLogo ?? "",
      companyEmailDomains: [...(detail.companyEmailDomains ?? [])],
      appraisalLenderNames: [...(detail.appraisalLenderNames ?? [])],
      onboardingEstimateRTLVolume: detail.onboardingEstimateRTLVolume ?? "",
      onboardingEstimateDSCRVolume: detail.onboardingEstimateDSCRVolume ?? "",
      onboardingEstimateGroundUpVolume:
        detail.onboardingEstimateGroundUpVolume ?? "",
      bank: {
        nameOfBank: detail.correspondentBankDetails?.nameOfBank ?? "",
        bankAccountOwnerName:
          detail.correspondentBankDetails?.bankAccountOwnerName ?? "",
        bankAccountOwnerType:
          detail.correspondentBankDetails?.bankAccountOwnerType ?? "",
        bankAccountNumber:
          detail.correspondentBankDetails?.bankAccountNumber ?? "",
        bankRoutingNumber:
          detail.correspondentBankDetails?.bankRoutingNumber ?? "",
        preferredPaymentMethod:
          detail.correspondentBankDetails?.preferredPaymentMethod ?? "",
      },
    }),
    [detail],
  );

  const [draft, setDraft] = useState(buildDraft);
  const [saving, setSaving] = useState(false);
  const [domainDraft, setDomainDraft] = useState("");
  const [domainError, setDomainError] = useState(null);
  const [lenderDraft, setLenderDraft] = useState("");

  const baseline = useMemo(() => buildDraft(), [buildDraft]);
  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(baseline),
    [draft, baseline],
  );

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setBank = (k, v) =>
    setDraft((d) => ({ ...d, bank: { ...d.bank, [k]: v } }));

  const addDomain = () => {
    const d = domainDraft.trim().toLowerCase().replace(/^@/, "");
    if (!d) return;
    if (FREE_DOMAINS.includes(d)) {
      setDomainError(`"${d}" is a public email provider.`);
      return;
    }
    if (draft.companyEmailDomains.includes(d)) {
      setDomainError(`"${d}" is already registered.`);
      return;
    }
    setDomainError(null);
    set("companyEmailDomains", [...draft.companyEmailDomains, d]);
    setDomainDraft("");
  };

  const addLender = () => {
    const v = lenderDraft.trim();
    if (!v || draft.appraisalLenderNames.includes(v)) return;
    set("appraisalLenderNames", [...draft.appraisalLenderNames, v]);
    setLenderDraft("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await patch({
        companyLogo: draft.companyLogo,
        companyEmailDomains: draft.companyEmailDomains,
        appraisalLenderNames: draft.appraisalLenderNames,
        onboardingEstimateRTLVolume: num(draft.onboardingEstimateRTLVolume),
        onboardingEstimateDSCRVolume: num(draft.onboardingEstimateDSCRVolume),
        onboardingEstimateGroundUpVolume: num(
          draft.onboardingEstimateGroundUpVolume,
        ),
        correspondentBankDetails: {
          ...(detail.correspondentBankDetails || {}),
          ...draft.bank,
        },
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PricingPageLayout nav={ADMIN_RAIL}>
        <ProfileSection
          id="admin-appraisal"
          title={`Appraisal lender names (${draft.appraisalLenderNames.length})`}
          desc="The correspondent's Lender/Client name(s) as they appear on their appraisals, including accepted aliases. An appraisal whose lender name matches none of these is flagged as ordered in another lender's name."
        >
          {draft.appraisalLenderNames.length > 0 && (
            <Flx wrap g={1} sx={{ mb: 1.5 }}>
              {draft.appraisalLenderNames.map((n) => (
                <Chip
                  key={n}
                  label={n}
                  onRemove={() =>
                    set(
                      "appraisalLenderNames",
                      draft.appraisalLenderNames.filter((x) => x !== n),
                    )
                  }
                />
              ))}
            </Flx>
          )}
          <Flx g={1} sx={{ maxWidth: 520, alignItems: "flex-end" }}>
            <TextField
              size="small"
              fullWidth
              value={lenderDraft}
              onChange={(e) => setLenderDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addLender()}
              placeholder="Lender name as printed on the appraisal"
              sx={FIELD_SX}
            />
            <Button
              variant="contained"
              onClick={addLender}
              startIcon={<AddRounded sx={{ fontSize: 16 }} />}
              sx={{ flexShrink: 0 }}
            >
              Add
            </Button>
          </Flx>
        </ProfileSection>

        <ProfileSection
          id="admin-bank"
          title="Bank details"
          desc="Where funds are sent to or received from this correspondent."
        >
          <Grid>
            <Cell span={4} spanSm={6}>
              <PricingField label="Bank name">
                <TextField
                  size="small"
                  fullWidth
                  value={draft.bank.nameOfBank}
                  onChange={(e) => setBank("nameOfBank", e.target.value)}
                  sx={FIELD_SX}
                />
              </PricingField>
            </Cell>
            <Cell span={4} spanSm={6}>
              <PricingField label="Account owner">
                <TextField
                  size="small"
                  fullWidth
                  value={draft.bank.bankAccountOwnerName}
                  onChange={(e) =>
                    setBank("bankAccountOwnerName", e.target.value)
                  }
                  sx={FIELD_SX}
                />
              </PricingField>
            </Cell>
            <Cell span={4} spanSm={6}>
              <PricingField label="Owner type">
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={draft.bank.bankAccountOwnerType}
                  onChange={(e) =>
                    setBank("bankAccountOwnerType", e.target.value)
                  }
                  sx={FIELD_SX}
                >
                  <MenuItem value="" sx={{ fontSize: 13, color: PT.faint }}>
                    — None —
                  </MenuItem>
                  {BANK_OWNER_TYPES.map((o) => (
                    <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>
                      {o}
                    </MenuItem>
                  ))}
                </TextField>
              </PricingField>
            </Cell>
            <Cell span={4} spanSm={6}>
              <PricingField label="Routing number">
                <TextField
                  size="small"
                  fullWidth
                  value={draft.bank.bankRoutingNumber}
                  onChange={(e) => setBank("bankRoutingNumber", e.target.value)}
                  sx={FIELD_SX}
                />
              </PricingField>
            </Cell>
            <Cell span={4} spanSm={6}>
              <PricingField label="Account number">
                <TextField
                  size="small"
                  fullWidth
                  value={draft.bank.bankAccountNumber}
                  onChange={(e) => setBank("bankAccountNumber", e.target.value)}
                  sx={FIELD_SX}
                />
              </PricingField>
            </Cell>
            <Cell span={4} spanSm={6}>
              <PricingField label="Preferred payment method">
                <TextField
                  size="small"
                  fullWidth
                  value={draft.bank.preferredPaymentMethod}
                  onChange={(e) =>
                    setBank("preferredPaymentMethod", e.target.value)
                  }
                  placeholder="Wire, check…"
                  sx={FIELD_SX}
                />
              </PricingField>
            </Cell>
          </Grid>
        </ProfileSection>

        <ProfileSection
          id="admin-branding"
          title="Branding"
          desc="How this partner's company appears on white-label surfaces."
        >
          <Grid>
            <Cell span={12}>
              <PricingField
                label="Logo URL"
                helperText="Used for white-label surfaces."
              >
                <TextField
                  size="small"
                  fullWidth
                  value={draft.companyLogo}
                  onChange={(e) => set("companyLogo", e.target.value)}
                  placeholder="https://…"
                  sx={FIELD_SX}
                />
              </PricingField>
            </Cell>
          </Grid>
        </ProfileSection>

        <ProfileSection
          id="admin-domains"
          title={`Email domains (${draft.companyEmailDomains.length})`}
          desc="Domains drive email attribution and gate who can sign up under this correspondent. Public providers are rejected and each domain must be unique across all partners."
        >
          {draft.companyEmailDomains.length > 0 && (
            <Flx wrap g={1} sx={{ mb: 1.5 }}>
              {draft.companyEmailDomains.map((d) => (
                <Chip
                  key={d}
                  label={d}
                  onRemove={() =>
                    set(
                      "companyEmailDomains",
                      draft.companyEmailDomains.filter((x) => x !== d),
                    )
                  }
                />
              ))}
            </Flx>
          )}
          <Flx g={1} sx={{ maxWidth: 520, alignItems: "flex-end" }}>
            <TextField
              size="small"
              fullWidth
              value={domainDraft}
              onChange={(e) => {
                setDomainDraft(e.target.value);
                setDomainError(null);
              }}
              onKeyDown={(e) => e.key === "Enter" && addDomain()}
              placeholder="add-domain.com"
              sx={FIELD_SX}
            />
            <Button variant="contained" onClick={addDomain} sx={{ flexShrink: 0 }}>
              Add
            </Button>
          </Flx>
          {domainError && (
            <Box sx={{ ...FIELD_TEXT.helper, color: statusColors.error.fg }}>
              {domainError}
            </Box>
          )}
        </ProfileSection>

        <ProfileSection
          id="admin-volumes"
          title="Onboarding volume estimates"
          desc="Annual volume the correspondent projected at onboarding."
        >
          <Grid>
            {[
              ["onboardingEstimateRTLVolume", "RTL / yr"],
              ["onboardingEstimateDSCRVolume", "DSCR / yr"],
              ["onboardingEstimateGroundUpVolume", "Ground Up / yr"],
            ].map(([key, label]) => (
              <Cell key={key} span={4} spanSm={4}>
                <PricingField label={label}>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    value={draft[key]}
                    onChange={(e) => set(key, e.target.value)}
                    placeholder="0"
                    sx={FIELD_SX}
                  />
                </PricingField>
              </Cell>
            ))}
          </Grid>
        </ProfileSection>
      </PricingPageLayout>

      <PendingChangesBar
        dirty={dirty}
        saving={saving}
        onSave={save}
        onDiscard={() => setDraft(buildDraft())}
        label="Save admin"
      />
    </>
  );
}
